/* global L */
import React, { useEffect, useRef, useState } from 'react';
import { Star } from "lucide-react";
import { Trash2, CheckCircle, Save } from "lucide-react";

const MAP_HOME_KEY = 'map_home_view';

function loadHomeView() {
  try {
    return JSON.parse(localStorage.getItem(MAP_HOME_KEY));
  } catch {
    return null;
  }
}

function saveHomeView(lat, lon, zoom) {
  localStorage.setItem(MAP_HOME_KEY, JSON.stringify({ lat, lon, zoom }));
}

function MapView({ telemetry, token, sendWSCommand, onHomeLocationUpdate, isPreview = false }) {
  const mapRef = useRef(null);
  const markersRef = useRef([]);
  const polylineRef = useRef(null);
  const droneRef = useRef(null);
  const homeMarkerRef = useRef(null);

  const [jumpLat, setJumpLat] = useState('');
  const [jumpLon, setJumpLon] = useState('');
  const [waypoints, setWaypoints] = useState([]);
  const [finalizedRoute, setFinalizedRoute] = useState(null);
  const [savedRoutes, setSavedRoutes] = useState([]);
  const [selectedRouteId, setSelectedRouteId] = useState('');
  const [message, setMessage] = useState('');
  const [homeLocation, setHomeLocation] = useState(null);

  // Helper: get valid token
  const getAuthToken = () => token || localStorage.getItem('token');

  // -------------------------------
  // INITIALIZE MAP
  // -------------------------------
  useEffect(() => {
    if (!mapRef.current || mapRef.current._leaflet_map) return;

    const home = loadHomeView();
    const map = L.map(mapRef.current, {
      zoomControl: !isPreview
    }).setView(
      // By default, Home Icon is in Vancouver. User can zoom out to see it globally
      home ? [home.lat, home.lon] : [49.2606, -123.2460],
      home?.zoom ?? 14
    );

    mapRef.current._leaflet_map = map;


    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '&copy; OpenStreetMap contributors',
    }).addTo(map);

    // Demo markers
    addIconMarker(map, [49.2635, -123.2550], 'bird');
    addIconMarker(map, [49.2665, -123.2500], 'human');

    map.on('click', (e) => {
      const { lat, lng } = e.latlng;
      addDraggableMarker(map, [lat, lng]);
    });

    fetchSavedRoutes();
    fetchHomeLocation();
  }, []);

  // Display home marker when homeLocation changes
  useEffect(() => {
    const map = mapRef.current?._leaflet_map;
    if (!map) return;

    if (homeLocation && homeLocation.lat && homeLocation.lon) {
      addHomeMarker(map, homeLocation.lat, homeLocation.lon);
    }
  }, [homeLocation]);

  // -------------------------------
  // DRONE MARKER
  // -------------------------------
  useEffect(() => {
    const map = mapRef.current?._leaflet_map;
    if (!map || !telemetry?.lat || !telemetry?.lon) return;

    const { lat, lon } = telemetry;

    if (!droneRef.current) {
      const droneIcon = L.divIcon({
        className: 'drone-icon',
        html: `<span style="font-size:28px;">🚁</span>`,
        iconSize: [30, 30],
        iconAnchor: [15, 15],
      });
      droneRef.current = L.marker([lat, lon], { icon: droneIcon, zIndexOffset: 9999 }).addTo(map);
    } else {
      droneRef.current.setLatLng([lat, lon]);
      if (!map.hasLayer(droneRef.current)) droneRef.current.addTo(map);
    }
  }, [telemetry]);

  // -------------------------------
  // WAYPOINTS
  // -------------------------------
  function addDraggableMarker(map, coords) {
    const marker = L.marker(coords, { draggable: true }).addTo(map);
    markersRef.current.push(marker);

    const updateWaypoints = () => {
      const updated = markersRef.current.map((m) => {
        const pos = m.getLatLng();
        return [pos.lat, pos.lng];
      });
      setWaypoints(updated);
    };

    marker.on('dragend', updateWaypoints);
    updateWaypoints();
  }

  useEffect(() => {
    const map = mapRef.current?._leaflet_map;
    if (!map) return;

    if (polylineRef.current) map.removeLayer(polylineRef.current);
    if (waypoints.length > 1) {
      polylineRef.current = L.polyline(waypoints, { color: 'blue' }).addTo(map);
    }
  }, [waypoints]);

  function addIconMarker(map, coords, type) {
    const icons = { bird: '🕊️', human: '🧍', default: '📍' };
    const icon = L.divIcon({
      className: 'custom-icon',
      html: `<span style="font-size: 24px;">${icons[type] || icons.default}</span>`,
      iconSize: [30, 30],
      iconAnchor: [15, 15],
    });
    L.marker(coords, { icon }).addTo(map).bindPopup(type);
  }

  function clearRoute() {
    const map = mapRef.current?._leaflet_map;
    if (!map) return;
    markersRef.current.forEach((m) => map.removeLayer(m));
    markersRef.current = [];
    if (polylineRef.current) map.removeLayer(polylineRef.current);
    polylineRef.current = null;
    setWaypoints([]);
    setFinalizedRoute(null);
  }

  function finalizeRoute() {
    if (waypoints.length > 2) {
      const closed = [...waypoints, waypoints[0]];
      setFinalizedRoute(closed);
      setWaypoints(closed);
    }
  }

  function jumpToCoords() {
    const map = mapRef.current?._leaflet_map;
    const lat = parseFloat(jumpLat);
    const lon = parseFloat(jumpLon);
    if (!map || isNaN(lat) || isNaN(lon)) return;
    map.setView([lat, lon], map.getZoom(), { animate: true });
  }

  function saveCurrentView() {
    const map = mapRef.current?._leaflet_map;
    if (!map) return;
    const center = map.getCenter();
    saveHomeView(center.lat, center.lng, map.getZoom());
  }

  // Notify parent when home location changes
  useEffect(() => {
    if (homeLocation && onHomeLocationUpdate) {
      onHomeLocationUpdate(homeLocation.lat, homeLocation.lon);
    }
  }, [homeLocation, onHomeLocationUpdate]);

  // -------------------------------
  // HOME LOCATION
  // -------------------------------
  const fetchHomeLocation = async () => {
    const authToken = getAuthToken();
    if (!authToken) return;

    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}/api/home-location`, {
        headers: { Authorization: `Bearer ${authToken}` },
      });

      if (res.ok) {
        const data = await res.json();
        if (data && data.lat && data.lon) {
          setHomeLocation({ lat: data.lat, lon: data.lon });
        }
      }
    } catch (err) {
      console.error('Error fetching home location:', err);
    }
  };

  const saveHomeLocation = async (lat, lon) => {
    const authToken = getAuthToken();
    if (!authToken) return;

    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}/api/home-location`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${authToken}`,
        },
        body: JSON.stringify({ lat, lon }),
      });

      if (res.ok) {
        setMessage(`Home location saved: (${lat.toFixed(4)}, ${lon.toFixed(4)})`);
        setHomeLocation({ lat, lon });
      }
    } catch (err) {
      console.error('Error saving home location:', err);
      setMessage('Failed to save home location');
    }
  };

  const addHomeMarker = (map, lat, lon) => {
    // Remove old marker if exists
    if (homeMarkerRef.current && map.hasLayer(homeMarkerRef.current)) {
      map.removeLayer(homeMarkerRef.current);
    }

    // Create home icon
    const homeIcon = L.divIcon({
      className: 'home-icon',
      html: `<span style="font-size:32px;">🏠</span>`,
      iconSize: [40, 40],
      iconAnchor: [20, 40],
    });

    const marker = L.marker([lat, lon], { icon: homeIcon, draggable: true }).addTo(map);
    
    marker.on('dragend', () => {
      const pos = marker.getLatLng();
      saveHomeLocation(pos.lat, pos.lng);
    });

    marker.bindPopup('Drag to set home location');
    homeMarkerRef.current = marker;
  };

  // -------------------------------
  // FETCH / SAVE / DELETE ROUTES
  // -------------------------------
  const fetchSavedRoutes = async () => {
    const authToken = getAuthToken();
    if (!authToken) {
      setMessage('No valid token — please log in');
      setSavedRoutes([]);
      return;
    }

    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}/api/routes`, {
        headers: { Authorization: `Bearer ${authToken}` },
      });

      if (!res.ok) {
        setSavedRoutes([]);
        setMessage('Failed to fetch saved routes');
        return;
      }

      const data = await res.json();
      setSavedRoutes(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error(err);
      setSavedRoutes([]);
      setMessage('Error fetching saved routes');
    }
  };

  async function saveRouteToDB() {
    if (!finalizedRoute || finalizedRoute.length === 0) {
      setMessage('No route finalized to save!');
      return;
    }

    const authToken = getAuthToken();
    if (!authToken) {
      setMessage('No valid token — please log in');
      return;
    }

    const pointsForDB = finalizedRoute.map(([lat, lon]) => ({ lat, lon }));

    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}/api/routes`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${authToken}`,
        },
        body: JSON.stringify({ name: 'Operator Route', points: pointsForDB }),
      });

      const data = await res.json();
      if (data.success) {
        setMessage(`Route saved! ID: ${data.id}`);
        fetchSavedRoutes();
      } else {
        setMessage(`Failed to save route: ${data.error || JSON.stringify(data)}`);
      }
    } catch (err) {
      console.error(err);
      setMessage('Error saving route: ' + err.message);
    }
  }

  const loadRoute = (id) => {
    const r = savedRoutes.find((r) => r.id === parseInt(id));
    if (!r) return;

    clearRoute();

    const map = mapRef.current._leaflet_map;

    // draw route on map
    r.points.forEach(({ lat, lon }) => addDraggableMarker(map, [lat, lon]));

    const coords = r.points.map(p => [p.lat, p.lon]);

    setFinalizedRoute(coords);
    setMessage(`Loaded route: ${r.name}`);

    // Send over WebSocket
    if (sendWSCommand) {
      sendWSCommand("route", {
        route_id: r.id,
        name: r.name,
        waypoints: r.points
      });
    }
  };

  const deleteRoute = async (id) => {
    const authToken = getAuthToken();
    if (!authToken) {
      setMessage('No valid token — please log in');
      return;
    }

    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}/api/routes/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${authToken}` },
      });

      if (res.ok) {
        setMessage('Route deleted');
        fetchSavedRoutes();
      } else {
        setMessage('Failed to delete route');
      }
    } catch (err) {
      console.error(err);
      setMessage('Error deleting route');
    }
  };

  // -------------------------------
  // RENDER
  // -------------------------------
  return (
    <div>
      {!isPreview && (
        <div style={{ marginBottom: '8px', fontSize: '12px', color: '#555' }}>
          Click on the map to add waypoints for your route.
        </div>
      )}

      <div
        ref={mapRef}
        id="map"
        style={{ height: '600px', width: '100%', borderRadius: '8px', marginBottom: '12px' }}
      />

      <div style={{ display: 'flex', gap: '12px', marginBottom: '12px' }}>
        <div className="control-row">
          <button className="control-btn accent" onClick={clearRoute}>
            <Trash2 size={12} style={{ marginRight: 6 }} />
            Clear Waypoints
          </button>

          <button className="control-btn accent" onClick={finalizeRoute}>
            <CheckCircle size={12} style={{ marginRight: 6 }} />
            Finalize Waypoints
          </button>

          <button className="control-btn accent" onClick={saveRouteToDB}>
            <Save size={12} style={{ marginRight: 6 }} />
            Save Route
          </button>
        </div>
      </div>

            
      {/* SAVED ROUTES PANEL */}
      <div
        style={{
          marginTop: "16px",
          padding: "12px",
          border: "1px solid #ddd",
          borderRadius: "8px",
          maxWidth: "430px", 
          background: "#fafafa"
        }}
      >
        <h4 style={{ margin: "0 0 8px 0", color: "#333" }}>Saved Routes</h4>

        <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
          <select
            value={selectedRouteId}
            onChange={(e) => setSelectedRouteId(e.target.value)}
            style={{
              flex: 1,
              padding: "6px 8px",
              borderRadius: "6px",
              border: "1px solid #ccc"
            }}
          >
            <option value="">Select a route…</option>
            {savedRoutes.map((r) => (
              <option key={r.id} value={r.id}>
                {r.name} — {new Date(r.created_at).toLocaleString()}
              </option>
            ))}
          </select>

          <button
            onClick={() => loadRoute(selectedRouteId)}
            disabled={!selectedRouteId}
            style={{ padding: "6px 10px" }}
          >
            Load
          </button>

          <button
            onClick={() => deleteRoute(selectedRouteId)}
            disabled={!selectedRouteId}
            style={{ padding: "6px 10px", background: "#dc2626", color: "white" }}
          >
            Delete
          </button>
        </div>

        <h4 style={{ margin: "4px 4px 8px 0", color: "#5e5959" }}>When you load a route, it's sent to the drone as a flight path.</h4>

      </div>

      {/* JUMP TO COORDS PANEL */}
      <div
        style={{
          marginTop: "16px",
          padding: "12px",
          border: "1px solid #ddd",
          borderRadius: "8px",
          maxWidth: "430px", 
          background: "#fafafa"
        }}
      >
        <h4 style={{ margin: "0 0 8px 0", color: "#333" }}>Jump to Map Coordinates</h4>

        <div style={{ display: "flex", gap: "8px" }}>
          <input
            type="number"
            step="0.00001"
            placeholder="Latitude"
            value={jumpLat}
            onChange={(e) => setJumpLat(e.target.value)}
            style={{
              flex: 1,
              padding: "6px 8px",
              borderRadius: "6px",
              border: "1px solid #ccc"
            }}
          />

          <input
            type="number"
            step="0.00001"
            placeholder="Longitude"
            value={jumpLon}
            onChange={(e) => setJumpLon(e.target.value)}
            style={{
              flex: 1,
              padding: "6px 8px",
              borderRadius: "6px",
              border: "1px solid #ccc"
            }}
          />

          <button onClick={jumpToCoords} style={{ padding: "6px 12px" }}>
            Go
          </button>
        </div>

        <button
          className="control-btn special save-view-btn"
          onClick={saveCurrentView}
        >
          <Star size={16} style={{ marginRight: "6px" }} />
          Save Current Map as Default View
        </button>
      </div>


      {message && <p style={{ color: 'green' }}>{message}</p>}
    </div>
  );
}

export default MapView;
